import { encodeCode128, formatBarcodeText, generateBarcodePath, isBarcodeSupported } from './BarcodeSVG';
import { generateQRCodeSVGString } from './QRCodeSVG';
import { formatParticularitiesText } from './ProductLabel';

/**
 * Generate pure HTML string for a single label
 */
export function generateSingleLabelHTML({
  product,
  selectedParticulars = null,
  customTopText = '',
  codeType = 'auto',
  showCutLine = true,
  showLateralId = true,
  showPrice = true,
}) {
  const id = product?.id || '';
  const name = (product?.name || '').toUpperCase();
  const barCode = String(product?.barCode || id || '000000').trim();
  const numValue = Number(product?.value) || 0;
  const [integerPart, centsPart] = numValue.toFixed(2).split('.');

  const topText =
    formatParticularitiesText(product?.particulars, selectedParticulars, customTopText) ||
    `REF: #${id || '0000'}`;

  // Check code support: fallback to QR Code if code is unsupported by 1D Barcode
  const effectiveCodeType =
    codeType === 'qrcode' || !isBarcodeSupported(barCode) ? 'qrcode' : 'barcode';

  let codeSvgHtml = '';
  if (effectiveCodeType === 'qrcode') {
    codeSvgHtml = generateQRCodeSVGString(barCode, 52, true);
  } else {
    // Encode barcode 1D
    const encoded = encodeCode128(barCode);
    if (encoded) {
      const quietZone = 8;
      const svgWidth = encoded.totalWidth + quietZone * 2;
      const barHeight = 32;
      const pathData = generateBarcodePath(encoded, quietZone, barHeight);
      const formattedBarcode = formatBarcodeText(barCode);

      codeSvgHtml = `
        <div style="display: flex; flex-direction: column; align-items: center; width: 100%;">
          <svg viewBox="0 0 ${svgWidth} ${barHeight}" preserveAspectRatio="none" style="width: 94%; height: 32px; shape-rendering: crispEdges; display: block;">
            <path d="${pathData}" fill="#000000" />
          </svg>
          <span style="font-family: 'Courier New', Courier, monospace; font-size: 9.5px; letter-spacing: 1.6px; font-weight: 700; color: #111; margin-top: 2px; text-align: center; line-height: 1;">
            ${formattedBarcode}
          </span>
        </div>
      `;
    }
  }

  return `
    <div class="print-label-card">
      <!-- 1. TOPO: Particularidade + ID Lateral -->
      <div class="print-top-row">
        <div class="print-particulars-box">
          <span class="print-particulars-text">${topText}</span>
        </div>
        ${
          showLateralId
            ? `<div class="print-lateral-id">#${id}</div>`
            : ''
        }
      </div>

      <!-- 2. CÓDIGO DE BARRAS OU QR CODE -->
      <div class="print-barcode-wrapper">
        ${codeSvgHtml}
      </div>

      <!-- 3. PICOTE COM TESOURA -->
      ${
        showCutLine
          ? `
        <div class="print-cut-line">
          <span class="print-scissors">✂</span>
          <div class="print-dashes"></div>
          <span class="print-scissors" style="transform: scaleX(-1);">✂</span>
        </div>
      `
          : ''
      }

      <!-- 4. PREÇO -->
      ${
        showPrice
          ? `
        <div class="print-price-section">
          <span class="print-currency">R$</span>
          <span class="print-integer">${integerPart}</span>
          <span class="print-cents">.${centsPart}</span>
          <span class="print-exponent">⁰</span>
        </div>
      `
          : ''
      }

      <!-- 5. NOME DO PRODUTO (campo 'name') -->
      <div class="print-product-name" title="${name}">
        ${name || 'PRODUTO SEM NOME'}
      </div>
    </div>
  `;
}

/**
 * Trigger label printing via invisible iframe
 * @param {Array} items - List of items: { product, quantity, selectedParticulars, customTopText }
 * @param {Object} options - { format: 'thermal' | 'a4', showCutLine, showLateralId, showPrice }
 */
export function printProductLabels(items = [], options = {}) {
  const {
    format = 'thermal', // 'thermal' or 'a4'
    codeType = 'auto',
    showCutLine = true,
    showLateralId = true,
    showPrice = true,
  } = options;

  if (!items || items.length === 0) return;

  // Flatten items by quantity
  const allLabelsHtml = [];
  items.forEach((item) => {
    const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
    const labelHtml = generateSingleLabelHTML({
      product: item.product,
      selectedParticulars: item.selectedParticulars,
      customTopText: item.customTopText,
      codeType: item.codeType || codeType,
      showCutLine,
      showLateralId,
      showPrice,
    });
    for (let i = 0; i < qty; i += 1) {
      allLabelsHtml.push(labelHtml);
    }
  });

  if (allLabelsHtml.length === 0) return;

  // Build Document HTML
  let contentHtml = '';
  if (format === 'a4') {
    // Exact 30 items per A4 sheet (3 columns x 10 rows)
    const pageSize = 30;
    const pages = [];
    for (let i = 0; i < allLabelsHtml.length; i += pageSize) {
      pages.push(allLabelsHtml.slice(i, i + pageSize));
    }
    contentHtml = pages
      .map(
        (pageItems, pageIdx) => `
      <div class="a4-sheet ${pageIdx < pages.length - 1 ? 'a4-page-break' : ''}">
        <div class="a4-page-grid">
          ${pageItems.map((h) => `<div class="a4-label-cell">${h}</div>`).join('')}
        </div>
      </div>
    `,
      )
      .join('');
  } else {
    // Thermal roll: each label in a page-breaking block
    contentHtml = allLabelsHtml
      .map((h) => `<div class="thermal-label-page">${h}</div>`)
      .join('');
  }

  const styles = `
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    html, body {
      background: #ffffff;
      color: #000000;
      font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .print-label-card {
      width: 72mm;
      min-height: 48mm;
      box-sizing: border-box;
      background: #ffffff;
      padding: 2.5mm 3.5mm;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .print-top-row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      gap: 6px;
      margin-bottom: 2mm;
      width: 100%;
    }

    .print-particulars-box {
      flex: 1;
      border: 2px solid #000000;
      border-radius: 2px;
      padding: 3px 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 24px;
      text-align: center;
    }

    .print-particulars-text {
      font-size: 14.5px;
      font-weight: 800;
      letter-spacing: 0.5px;
      line-height: 1.15;
      text-transform: uppercase;
      word-break: break-word;
    }

    .print-lateral-id {
      display: flex;
      align-items: center;
      justify-content: center;
      writing-mode: vertical-rl;
      transform: rotate(180deg);
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 1px;
      color: #111111;
      padding: 0 2px;
      white-space: nowrap;
    }

    .print-barcode-wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 1mm 0 1.5mm 0;
    }

    .print-cut-line {
      display: flex;
      align-items: center;
      width: 100%;
      margin: 1.5mm 0;
      color: #222222;
    }

    .print-scissors {
      font-size: 13px;
      line-height: 1;
      font-weight: bold;
    }

    .print-dashes {
      flex: 1;
      border-bottom: 1.5px dashed #333333;
      margin: 0 4px;
      height: 0;
    }

    .print-price-section {
      display: flex;
      align-items: baseline;
      justify-content: center;
      margin-top: 1.5mm;
      margin-bottom: 1mm;
      width: 100%;
    }

    .print-currency {
      font-size: 16px;
      font-weight: 800;
      margin-right: 6px;
    }

    .print-integer {
      font-size: 34px;
      font-weight: 900;
      line-height: 1;
      letter-spacing: -0.5px;
    }

    .print-cents {
      font-size: 19px;
      font-weight: 800;
      vertical-align: super;
      margin-left: 2px;
      position: relative;
      top: -8px;
    }

    .print-exponent {
      font-size: 11px;
      font-weight: 900;
      margin-left: 1px;
      vertical-align: super;
      position: relative;
      top: -4px;
    }

    .print-product-name {
      font-size: 12px;
      font-weight: 800;
      line-height: 1.25;
      text-transform: uppercase;
      color: #000000;
      margin-top: 1mm;
      padding-left: 2mm;
      word-break: break-word;
      max-height: 2.8em;
      overflow: hidden;
    }

    ${
      format === 'a4'
        ? `
      @page {
        size: A4 portrait;
        margin: 8mm 6mm;
      }
      .a4-sheet {
        width: 100%;
        box-sizing: border-box;
      }
      .a4-page-break {
        page-break-after: always;
        break-after: page;
      }
      .a4-page-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 3mm 4mm;
        width: 100%;
      }
      .a4-label-cell {
        page-break-inside: avoid;
        break-inside: avoid;
        border: 1px dashed #e2e8f0;
      }
      @media print {
        .a4-label-cell {
          border: none;
        }
      }
    `
        : `
      @page {
        size: 75mm auto;
        margin: 0;
      }
      .thermal-label-page {
        width: 75mm;
        page-break-after: always;
        break-after: page;
        display: flex;
        justify-content: center;
        padding: 1.5mm 1.5mm;
      }
    `
    }
  `;

  // Remove any previous print iframe to prevent DOM duplicates and state collision
  const oldIframe = document.getElementById('labelPrintIframe');
  if (oldIframe && oldIframe.parentNode) {
    oldIframe.parentNode.removeChild(oldIframe);
  }

  const iframe = document.createElement('iframe');
  iframe.id = 'labelPrintIframe';
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
  });

  document.body.appendChild(iframe);

  const fullHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Imprimir Etiquetas</title>
        <style>
          @media print {
            ${styles}
          }
          body {
            font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
            background: #ffffff;
          }
        </style>
      </head>
      <body>
        ${contentHtml}
      </body>
    </html>
  `;

  // Write content first
  try {
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(fullHtml);
    doc.close();
  } catch (err) {
    console.error('Error writing to print iframe:', err);
  }

  let printed = false;
  const doPrint = () => {
    if (printed) return;

    // Verify document has content to avoid printing blank about:blank
    try {
      const body = iframe.contentWindow?.document?.body;
      if (!body || !body.children.length) {
        return;
      }
    } catch (e) {}

    printed = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      console.error('Print failed', e);
    }
  };

  // Wait for iframe load event after doc.close(), with fallback
  iframe.onload = () => {
    setTimeout(doPrint, 400);
  };

  // Safety fallback in case onload already fired synchronously upon doc.close()
  setTimeout(doPrint, 700);

  // Clean up iframe when print dialog finishes (onafterprint) or upon next print call
  if (iframe.contentWindow) {
    iframe.contentWindow.onafterprint = () => {
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 500);
    };
  }
}
